import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from "react-dom";
import { getBasketData, refetchBasketData } from "../queries/global";

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import TickIcon from "../svg/tick";
import InfoCircle from "../svg/info-circle";
import SeatLockIcons from '../svg/seatlock-icons';
import { useMediaQuery } from '../hook/useMediaQuery';
import { SeatSelectionSummaryItem } from './seat-selection-summary';
import LoadingIndicator from '../components/loading-indicator';

const max_tickets_bookable = parseInt(config.OPTION_FIELDS.max_tickets_bookable);

export default function SeatButton(props) {

    const [status, setStatus] = useState(null);
    const [seatBand, setSeatBand] = useState(null);
    const [seatColor, setSeatColor] = useState(null);
    const [ticketTypeId, setTicketTypeId] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [seatInfo, setSeatInfo] = useState(null);
    const [lockType, setLockType] = useState(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({x: 0, y: 0});

    const { data: basketData, isFetching: basketDataFetching } = getBasketData();

    const isMobile = useMediaQuery(`(max-width: 768px)`);

    const {
        data: priceListData,
    } = useQuery({
        queryKey: ["priceListData", config.INSTANCE_ID],
        queryFn: () => QUERIES.getInstancePriceList(config.INSTANCE_ID),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        
        // Get seat status
        if (!props.statusData || !props.statusData.status[props.seat.id] || props.seatTicketIsPending) return;
        
        // Map statuses to seats
        const statusMap = {
            A: "is-available",
            B: "is-in-basket",
            U: "is-unavailable",
            W: "is-wheelchair-locked",
            X: "is-locked"
        };
        const seat_status = statusMap[props.statusData.status[props.seat.id].status] || "";
        setStatus(seat_status);

        if (seat_status == "is-in-basket") {
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
                
        // Check for lock types
        if (seat_status == "is-wheelchair-locked") {
            setLockType("Wheelchair Seat");
        }

        if (props.statusData.status[props.seat.id].lockType) {
            const lock_type = props.statusData.status[props.seat.id].lockType.toLowerCase();

            // Sometimes client's don't use the lock type correctly so this logic is here to ensure that Companion 
            // and Wheelchair lock types get the correct icons even if the status isn't correct
            if (lock_type.startsWith("e -") || lock_type.includes("companion") || lock_type.includes("escort")) {
                setLockType(props.statusData.status[props.seat.id].lockType);
            } else if (lock_type.includes("wheelchair") || seat_status == "is-wheelchair-locked") {
                setLockType("Wheelchair Seat");
            } else if (lock_type.includes("caption")) {
                setLockType("Closed Captions");
            } else if (lock_type.includes("audio")) {
                setLockType("Audio Description");
            } else if (lock_type.includes("bsl")) {
                setLockType("British Sign Language");
            } else if (lock_type.includes("relaxed")) {
                setLockType("Relaxed Performance");
            } else {
                setLockType(props.statusData.status[props.seat.id].lockType);
            }
        }

        // Find out if there is extra info
        if (props.statusData.info[props.seat.id]) {
            setSeatInfo(props.statusData.info[props.seat.id]);
        }

        // Match the seat band id to the instance pricelist to find the band colour
        let seat_band = priceListData?.find((price) => {

            return price.priceBand.id == props.statusData.status[props.seat.id].priceBandId
                    && config.OPTION_FIELDS.default_ticket_type_name.includes(price.ticketType.name)
        });

        if (!seat_band) return;

        // Set the ticket type ID to be used in the add/remove queries
        setTicketTypeId(seat_band.ticketType?.id);
        setSeatBand(seat_band);

        if (seat_status == "is-available" || seat_status == "is-in-basket") {
            // Allows enabling of filter buttons in select-seats when data is available
            props.setEnabledFilters(prev => {
                const updated = [...prev];
                if (props.area && !updated.includes(props.area.id)) updated.push(props.area.id);
                
                const bandAmountStr = seat_band.amount.toString();
                if (!updated.includes(bandAmountStr)) updated.push(bandAmountStr);

                return updated;
              });
        }
        
    }, [props.statusData, priceListData]);

    useEffect(() => {
        if (!props.groupedPrices || !config.COLORS || !seatBand) return;

        const matched_price = props.groupedPrices.find((price) => price.amount == seatBand.amount);
        setSeatColor(matched_price?.color);
    }), [props.groupedPrices, seatBand];
    
    useEffect(() => {

        // Toggle button filtering by price and area
        // The disabled prop also gives the button un available styles. 
        // If you don't want unavailable styles set the disabled attribute directly in the render
        if  (   
                (props.selectedBands && seatBand && !props.selectedBands.includes(seatBand.amount)) ||
                (props.selectedArea && props.selectedArea.id != props.area?.id)
            ) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
        
    }, [props.selectedBands, seatBand, props.selectedArea, props.selectedSeatCount]);

     
    
    const handleOnClick = () => {
        if (isSelected) {
            let seat_basket_id = null;
    
            if (basketData) {
                // Get basket id from basket data
                const basket_item = UTILS.getBasketTicketById(basketData, props.seat.id);
                seat_basket_id = basket_item?.id;
            }
            
            if (!seat_basket_id) return;
            
            props.removeTicket(seat_basket_id);
            // setTicket(null);
    
            // TO DO: Set this to "is-info" if that is the original state
            setStatus("is-available");
            setIsSelected(false);
        }
        if (!isSelected) {
            props.addTicket({ticketTypeId, seatId: props.seat.id});
            setIsSelected(true);
        }
    }
    
    const handleOnHover = (e, isEnter) => {
        if(isEnter) {
            setTooltipPosition({
                x: e.clientX,
                y: e.clientY,
            })
        }

        setShowTooltip(isEnter);
    }


    if (!props.seat || !status) return null;

    if ((status === "is-unavailable" || status === "is-sold") && !isSelected) {        
        return (
            <p className={`seating-plan__seat ${status}`}
                style={{top: props.seat.y+"px", left: props.seat.x+"px"}}>
                <span className='sr-only'>Seat {props.seat.name} is unavailable</span>
            </p>
        )
    }
// console.log("config.OPTION_FIELDS.default_ticket_type_name", config.OPTION_FIELDS.default_ticket_type_name)
    return (
        <>
        <button type="button" 
                // onMouseEnter={(e) => handleOnHover(e, true)}
                onMouseMove={(e) => handleOnHover(e, true)}
                onMouseLeave={(e) => handleOnHover(e, false)}
                className={`seating-plan__seat ${status} ${disabled ? 'disabled' : ''} ${props.seat.name} ${props.isWidow ? 'is-widow' : ''}`}
                style={{top: props.seat.y+"px", left: props.seat.x+"px", backgroundColor: seatColor ? seatColor : "", color: seatColor ? seatColor : ""}}
                aria-selected={isSelected}
                disabled={
                    disabled || 
                    (isSelected && (basketDataFetching || props.seatTicketIsPending)) || 
                    (!isSelected && (max_tickets_bookable && props.selectedSeatCount > max_tickets_bookable))
                }
                onClick={(e) => {handleOnClick(e)} }>
                { isSelected ? 
                    <>
                        <span className='sr-only'>Deselect seat {props.seat.name}</span>
                        <TickIcon /> 
                    </>
                    : 
                    <span className='sr-only'>Select seat {props.seat.name}</span> 
                }

                {(lockType || seatInfo) && (
                    <span className='seating-plan__more-info'>
                        {lockType ? (
                            <>
                                <strong className='sr-only'>{lockType}</strong>
                                <SeatLockIcons icon={lockType.trim()} />
                            </>
                        ) : seatInfo ? (
                            <>
                                <strong className='sr-only'>{seatInfo?.priceBandId?.replaceAll("+", " ")}</strong>
                                <InfoCircle />
                            </>
                        ) : null}
                    </span>
                )}

                {showTooltip && !isMobile && seatInfo && 
                    createPortal(
                        <span className="tooltiptext"
                            style={{left: tooltipPosition.x, top: tooltipPosition.y}}
                        >
                            <InfoCircle /> <span><strong>{props.seat.name + ' - £' + seatBand?.amount.toFixed(2)}</strong> - {decodeURIComponent(seatInfo.priceBandId.replaceAll("+"," "))}</span>
                        </span>, document.querySelector(".seating-plan"))
                }
        </button>
        </>
    );
}


